import { useEffect, useState } from "react";

import type { Devices, ById, ByType } from "../../types/components/webcam/devicesTypes";
import type { UseCamera } from "../../types/components/webcam/useCameraTypes";
import type { UseRecorder } from "../../types/components/webcam/useRecorderTypes";
import type { Recording } from "../../types/components/webcam/useRecordingStoreTypes";
import type { Options, UseRecordWebcamArgs, UseRecordWebcam } from "../../types/components/webcam/useRecordWebcamTypes";
import { getDevices } from "./devices";
import { useCamera } from "./useCamera";
import { useRecorder } from "./useRecorder";

const isDevMode = false;

/**
 * React Record Webcam hook.
 * @param args Configuration options for the hook.
 * @returns {UseRecordWebcam} providing access to webcam recording functionalities.
 */
export function useRecordWebcam({
	mediaRecorderOptions,
	mediaTrackConstraints,
	options,
}: Partial<UseRecordWebcamArgs> = {}): UseRecordWebcam {
	const [devices, setDevices] = useState<Devices>();
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	function handleError(functionName: string, error: any): void {
		if (isDevMode) {
			console.error(`@${functionName}: `, error);
		}

		const message = typeof error === "string" ? error : typeof error.message === "string" ? error.message : "";
		setErrorMessage(message);
	}

	function clearError(): void {
		setErrorMessage(null);
	}

	const { applyConstraints, closeCamera, openCamera } = useCamera({
		mediaTrackConstraints,
		handleError,
	});
	const {
		activeRecordings,
		applyRecordingOptions,
		cancelRecording,
		clearAllRecordings,
		clearPreview,
		createRecording,
		download,
		muteRecording,
		pauseRecording,
		resumeRecording,
		startRecording,
		stopRecording,
	} = useRecorder({ mediaRecorderOptions, options, devices, handleError });

	async function init() {
		try {
			const devices = await getDevices();
			setDevices(devices);
		} catch (error) {
			handleError("init", error);
		}
	}

	useEffect(() => {
		init();
		return () => {
			clearAllRecordings();
		};
	}, []);

	return {
		activeRecordings,
		applyConstraints,
		applyRecordingOptions,
		cancelRecording,
		clearAllRecordings,
		clearError,
		clearPreview,
		closeCamera,
		createRecording,
		devicesById: devices?.devicesById,
		devicesByType: devices?.devicesByType,
		download,
		errorMessage,
		muteRecording,
		openCamera,
		pauseRecording,
		resumeRecording,
		startRecording,
		stopRecording,
	};
}
